import { lazy, Suspense, useEffect } from "react";
import bridge from "../turbo/bridge";
import { createRoot } from "react-dom/client";
import { QueryClientProvider } from "react-query";
import { Provider } from "react-redux";
import store from "../store";
import queryClient from "../helpers/queryClient";
import {
  HeaderPortal,
  UserbarPortal,
  ChatbarPortal,
  ContentPortal,
} from "./layout/portals.tsx";
import TixxtUserbar from "./layout/TixxtUserbar";
import TixxtChatbar from "./layout/TixxtChatbar";
import TixxtContent from "./layout/TixxtContent";
import TixxtHeader from "./layout/TixxtHeader";
import { StaticTixxtGroupNavigation } from "./layout/TixxtGroupNavigation.tsx";
import { StaticTixxtPageTitle } from "./layout/TixxtPageTitle";
import { BrowserRouter } from "../helpers/tixxt-router";
import AppSearchHeader from "./layout/AppSearchHeader";
import SessionWatchdog from "./SessionWatchdog";
import useDeviceRegistrationRefresh from "../hooks/useDeviceRegistrationRefresh";
import { TurboFrameMissingEvent } from "@hotwired/turbo";

const ThemeOverrides = lazy(() => import("./administration/ThemeOverrides"));

// For debugging only
document.addEventListener("turbo:visit", function (event) {
  window.debug("⚡️ turbo:visit", new URL(event["detail"].url).pathname);
});

// Fix Content Missing outside of development
// In development it produces an error and should be fixed
if (!window.Settings?.development) {
  document.addEventListener("turbo:frame-missing", function (
    event: TurboFrameMissingEvent,
  ) {
    event.preventDefault();
    event.detail.visit(event.detail.response, {});
  } as EventListener);
}

/*
 SPA entry point
 */
export function launchApp() {
  function setupReact() {
    const container = document.getElementById("app-root");
    const root = createRoot(container!);
    root.render(<App />);
    document.removeEventListener("turbo:load", setupReact);
  }

  // Setup only once
  document.addEventListener("turbo:load", setupReact);
}

/*
 * Main frontend component
 */
export default function App() {
  const currentMember = Preload.current_member;
  const canChat = currentMember?.can.chat;

  useEffect(() => {
    if (window.isApp) {
      // Do NOT report isLoggedIn on /legal (not even false — that would send
      // the app to the network selection): current_member is present there,
      // but the native app would immediately hand the webview over to the
      // main app before the legal texts are acknowledged (#104). After
      // confirming (/legal -> root) the effect fires normally.
      if (window.location.pathname !== "/legal") {
        bridge.setMemberIsLoggedIn(currentMember != null);
      }
      if (currentMember != null) {
        bridge.setMemberId(currentMember.id);
      }
      bridge.setMemberCanChat(!!canChat);
    }
  }, [currentMember, canChat]);

  useDeviceRegistrationRefresh(currentMember);

  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          {currentMember ? (
            <>
              <SessionWatchdog />
              <Suspense fallback={null}>
                <ThemeOverrides />
              </Suspense>
            </>
          ) : null}
          <HeaderPortal>
            {window.isApp ? <AppSearchHeader /> : <TixxtHeader />}
          </HeaderPortal>
          <UserbarPortal>
            <TixxtUserbar />
          </UserbarPortal>
          {canChat &&
            !window.isApp &&
            window.location.pathname !== "/legal" && (
              <ChatbarPortal>
                <TixxtChatbar />
              </ChatbarPortal>
            )}
          <StaticTixxtGroupNavigation />
          <StaticTixxtPageTitle />
          {window.Turbo?.session.drive ? (
            <ContentPortal>
              <TixxtContent />
            </ContentPortal>
          ) : null}
        </BrowserRouter>
      </QueryClientProvider>
    </Provider>
  );
}
